import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { FullScreenLoading } from '@backrs_ui/common-ui';

import { Header } from '@backrs_ui/grow-ui/src/components/Lubav/Header';
import { MobileViewToggle } from '@backrs_ui/grow-ui/src/components/Lubav/MobileViewToggle';
import { sessionNames } from '@backrs_ui/grow-ui/src/components/SolutionSession/SessionStrings';
import { SolutionSessionContent } from '@backrs_ui/grow-ui/src/components/SolutionSession/SolutionSessionContent';
import { isStructuredSession, SolutionSessionId } from '@backrs_ui/grow-ui/src/models/models';
import { useAppDispatch, useAppSelector } from '@backrs_ui/grow-ui/src/redux/hooks';
import { setActiveSessionType } from '@backrs_ui/grow-ui/src/redux/lubavSessionState/lubavSessionState';
import { getSessionState } from '@backrs_ui/grow-ui/src/redux/lubavSessionState/lubavSessionState.utils';
import {
  selectShareToken,
  selectSharedSolutionSessionData,
  updateSharedSolutionSessionData,
} from '@backrs_ui/grow-ui/src/redux/sharedSession/sharedSession';
import { useGetSharedSolutionSessionDataQuery } from '@backrs_ui/grow-ui/src/redux/sharedSession/sharedSession.api';
import { useGetMostRecentResumeBuilderSubsessionsQuery } from '@backrs_ui/grow-ui/src/redux/solutionSession/solutionSession.api';
import { loadSessionData } from '@backrs_ui/grow-ui/src/redux/solutionSession/solutionSession.thunk';

import { Box, SxProps, Theme, Typography, useMediaQuery } from '@mui/material';

import { StickerCardId, useToolCardById } from '../Home/hooks/stickerCardMapping';

export const SharedSolutionSessionPage = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { uuid, sessionType } = useParams<{ uuid: string; sessionType: string }>();

  // redirect old solution session share links to the new office hours share links
  const isMigratedSession = isStructuredSession(sessionType);

  useEffect(() => {
    if (uuid && isMigratedSession) {
      navigate(`/shared/${uuid}/office-hours/${sessionType}`, { replace: true });
    }
  }, [uuid, sessionType, isMigratedSession, navigate]);

  const shareToken = useAppSelector(selectShareToken);
  const solutionSessionData = useAppSelector(selectSharedSolutionSessionData);

  const isMobile = useMediaQuery('(max-width: 900px)');
  const [mobileView, setMobileView] = useState<'chat' | 'info'>('info');

  const { getStickerCard } = useToolCardById({ navigate });

  const { data, isLoading, error } = useGetSharedSolutionSessionDataQuery(
    {
      accessToken: shareToken || '',
      requestedSessionType: sessionType,
    },
    { skip: !shareToken || isMigratedSession },
  );

  const isResumeBuilder = sessionType?.startsWith('resume_builder');

  useGetMostRecentResumeBuilderSubsessionsQuery(undefined, {
    skip: !shareToken || !isResumeBuilder,
  });

  const activeSession = (sessionType || solutionSessionData?.session_type) as SolutionSessionId;

  // Get boxData to check if we should disable mobile toggle
  const { boxData } = useAppSelector((state) => getSessionState(state, activeSession).boxData);

  useEffect(() => {
    if (data) {
      dispatch(updateSharedSolutionSessionData(data));

      // Set the active session type
      const dataSessionType = data.session_type as SolutionSessionId;

      dispatch(setActiveSessionType(dataSessionType));

      if (data.session_id) {
        dispatch(
          loadSessionData({
            sessionData: { ...data, session_id: data.session_id },
            activeSession: dataSessionType,
          }),
        );
      }
    }
  }, [data, dispatch]);

  if (isLoading || !shareToken) {
    return <FullScreenLoading />;
  }

  if (error || !solutionSessionData) {
    return (
      <Box sx={errorContainerSx}>
        <Typography variant="text-lg-semibold">
          Unable to load shared session. The link may have expired or been revoked.
        </Typography>
      </Box>
    );
  }

  const sessionName = sessionNames[activeSession] || 'Solution Session';
  const stickerCard = getStickerCard(activeSession as StickerCardId);
  const stickerUrl = stickerCard?.stickerUrl || '/backrs.svg';

  const toggleMobileView = () => {
    setMobileView(mobileView === 'chat' ? 'info' : 'chat');
  };

  return (
    <Box sx={containerSx}>
      <Header
        title={sessionName}
        description={stickerCard?.description}
        stickerUrl={stickerUrl}
        leftComponent={
          isMobile ? (
            <MobileViewToggle
              onClick={toggleMobileView}
              type={mobileView === 'chat' ? 'CHAT' : 'RECS'}
              disabled={!boxData?.mainResponse}
            />
          ) : undefined
        }
        newChatLabel=""
      />
      <SolutionSessionContent mobileView={mobileView} keyboardHeight={0} />
    </Box>
  );
};

const containerSx: SxProps<Theme> = {
  flexGrow: 1,
  display: 'flex',
  flexDirection: 'column',
  boxSizing: 'border-box',
  maxWidth: '100dvw !important',
};

const errorContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: '100vh',
  padding: backrs.spacing['spacing-xl'],
  textAlign: 'center',
});
